import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { handleAuthError } from '../utils/auth';

const API_BASE_URL = 'https://best3intennis.com/best3intennis-api/api';

/**
 * SECURITY: HTML entity encoding helper to prevent DOM XSS vulnerabilities
 * when generating printable/exportable HTML documents.
 */
const escapeHtml = (str) => {
  if (str == null) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
};

/**
 * Formats inline markdown safely (escapes HTML first, then applies safe formatting).
 */
const formatInlineMarkdown = (str) => {
  if (!str) return '';
  const escaped = escapeHtml(str);
  return escaped.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
};

/**
 * Strips any preamble, prompt echoes, or scratchpad notes from the raw markdown,
 * and normalizes old and new AI analyses into formal report sections.
 */
const cleanAnalysisMarkdown = (md) => {
  if (!md) return '';
  let text = md.trim();

  // Strip thinking blocks
  text = text.replace(/<thought>[\s\S]*?<\/thought>/gi, '');
  text = text.replace(/```thought[\s\S]*?```/gi, '');

  // Convert bulleted or bolded section headers (e.g. "* *Overview:*", "**Overview:**", "* Overview:", etc.) into formal ## headers
  text = text.replace(/^[ \t]*[-*•]?[ \t]*\*?\*?(Executive Summary|Overview)\*?\*?:?[ \t]*/gmi, '\n## 📋 Overview\n');
  text = text.replace(/^[ \t]*[-*•]?[ \t]*\*?\*?(Main Issues|Performance Breakdown|Areas for Improvement|Weaknesses|Mistake Analysis|Key Metrics|Performance Pillars)\*?\*?:?[ \t]*/gmi, '\n## 📊 Performance Breakdown\n');
  text = text.replace(/^[ \t]*[-*•]?[ \t]*\*?\*?(Recommendation for Improvement|Recommendations for Improvement|Tactical Recommendations|Recommendations|Actionable Recommendations)\*?\*?:?[ \t]*/gmi, '\n## 💡 Recommendation for Improvement\n');
  text = text.replace(/^[ \t]*[-*•]?[ \t]*\*?\*?(Recommended Practice Drills|Practice Drills|On-Court Drills|Drills)\*?\*?:?[ \t]*/gmi, '\n## 🎾 Recommended Practice Drills\n');
  text = text.replace(/^[ \t]*[-*•]?[ \t]*\*?\*?(Recommendation to Talk to Your Coach About|Topics to Discuss with Your Coach|Talk to Your Coach|Coach Consultation Topics|Coach Discussion Points|Coach Discussion)\*?\*?:?[ \t]*/gmi, '\n## 🗣️ Recommendation to Talk to Your Coach About\n');

  // Normalize section headers that start with #
  text = text.replace(/^#{1,3}\s*📋?\s*(Executive Summary|Overview)\b.*$/gmi, '## 📋 Overview');
  text = text.replace(/^#{1,3}\s*📊?\s*(Performance Breakdown|Key Metrics|Match Breakdown|Performance Pillars)\b.*$/gmi, '## 📊 Performance Breakdown');
  text = text.replace(/^#{1,3}\s*💡?\s*(Recommendation for Improvement|Recommendations for Improvement|Tactical Recommendations|Actionable Recommendations|Areas for Improvement)\b.*$/gmi, '## 💡 Recommendation for Improvement');
  text = text.replace(/^#{1,3}\s*🎾?\s*(Recommended Practice Drills|Practice Drills|On-Court Drills|Drills)\b.*$/gmi, '## 🎾 Recommended Practice Drills');
  text = text.replace(/^#{1,3}\s*🗣️?\s*(Recommendation to Talk to Your Coach About|Topics to Discuss with Your Coach|Talk to Your Coach|Coach Consultation Topics|Coach Discussion Points)\b.*$/gmi, '## 🗣️ Recommendation to Talk to Your Coach About');

  // Locate the start of the formal report (first ## header)
  const firstH2 = text.search(/^##\s+/m);
  if (firstH2 !== -1) {
    text = text.substring(firstH2);
  }

  // Split multiple numbered items embedded on the same line (e.g. "1. *A*: ... 2. *B*: ... 3. *C*: ...")
  text = text.replace(/(?<!\n)(\s+)(\d+\.\s+\*?\*?[A-Za-z])/g, '\n\n$2');

  // If Performance Breakdown is not formatted as a table, convert metric bullets into a table
  if (text.includes('## 📊 Performance Breakdown') && !text.includes('| :---')) {
    text = text.replace(/## 📊 Performance Breakdown([\s\S]*?)(?=## 💡|## 🎾|## 🗣️|$)/i, (match, body) => {
      if (body.includes('* Winners') || body.includes('Winners vs') || body.includes('Hold Rate') || body.includes('Error Distribution')) {
        const rawItems = body.split(/(?:^|\n)[-*•]\s+/).map(s => s.trim()).filter(Boolean);
        let table = '\n\n| Performance Pillar | Match Statistics | Tactical Assessment & Impact |\n| :--- | :--- | :--- |\n';
        rawItems.forEach(item => {
          const parts = item.split(/:\s*/);
          if (parts.length >= 2) {
            const pillar = parts[0].replace(/[*_]/g, '').trim();
            const rest = parts.slice(1).join(': ').trim();
            const sentences = rest.split(/\.\s+/);
            const stat = sentences[0].replace(/[*_]/g, '').trim();
            const impact = sentences.slice(1).join('. ').replace(/[*_]/g, '').trim() || 'Key statistical driver for performance tuning';
            table += `| **${pillar}** | ${stat} | ${impact} |\n`;
          }
        });
        return `## 📊 Performance Breakdown\n${table}\n\n`;
      }
      return match;
    });
  }

  // Filter out any trailing self-checks or meta labels
  const lines = text.split('\n');
  const cleanLines = [];
  const seenHeaders = {};

  for (const l of lines) {
    const trimmed = l.trim();
    if (/^[-*•]?\s*\*?(Check|Self-check|Constraint|Check headers|Check terminology|Remove fluff|Check constraints|Let me know if|Did I use|Are the headers|Is the tone)\*?:?/i.test(trimmed)) continue;
    if (/^[-*•]?\s*(Role:|Task:|Constraints:|Data Provided:|Terminology:|Format:|Instructions:)/i.test(trimmed)) continue;
    if (/(Wait, the data says|I will treat this as|I must use the data|Actually, if there are|no 4-space indentation|Table \+ Paragraph|Rep Goal\)|2-3 points \+ questions)/i.test(trimmed)) continue;
    if (/^\d+\.\s*##/i.test(trimmed)) continue;
    if (/`?##\s*[^`,\n]+`?,\s*`?##\s*/i.test(trimmed)) continue;
    if (/^\s*`?##\s*[^\n,]+`?,\s*`?##/i.test(trimmed)) continue;

    // Deduplicate consecutive or identical section headers
    if (/^##\s+(.*)$/.test(trimmed)) {
      const hMatch = trimmed.match(/^##\s+(.*)$/);
      const headerName = hMatch ? hMatch[1].trim() : '';
      if (seenHeaders[headerName]) {
        continue;
      }
      seenHeaders[headerName] = true;
    }

    // Remove artificial bullet points placed before numbered items (e.g. "* 1. " -> "1. ")
    let processedLine = l.replace(/^[-*•]\s+(\d+\.\s+)/, '$1');
    // Unindent leading spaces
    processedLine = processedLine.replace(/^[ ]{2,6}(?=\d+\.|\*|-|#|[A-Za-z])/, '');

    cleanLines.push(processedLine);
  }

  return cleanLines.join('\n').trim();
};

/**
 * Report Component using react-markdown with custom theme components for headings, callouts, and paragraphs.
 */
const ReportMarkdownRenderer = ({ content }) => {
  if (!content) return null;
  const cleaned = cleanAnalysisMarkdown(content);

  const getHeadingStyle = (children) => {
    const text = Array.isArray(children) ? children.join('') : String(children || '');
    if (text.includes('Performance Breakdown') || text.includes('Main Issues') || text.includes('Issue') || text.includes('Weakness') || text.includes('Mistake')) {
      return {
        card: 'bg-rose-50/80 border-rose-200 text-rose-900',
        badge: 'bg-rose-100 text-rose-800 border-rose-200',
        icon: '📊',
      };
    }
    if (text.includes('Improvement') || text.includes('Recommendation') || text.includes('Tactical')) {
      return {
        card: 'bg-emerald-50/80 border-emerald-200 text-emerald-900',
        badge: 'bg-emerald-100 text-emerald-800 border-emerald-200',
        icon: '💡',
      };
    }
    if (text.includes('Drill') || text.includes('Practice')) {
      return {
        card: 'bg-indigo-50/80 border-indigo-200 text-indigo-900',
        badge: 'bg-indigo-100 text-indigo-800 border-indigo-200',
        icon: '🎾',
      };
    }
    if (text.includes('Coach') || text.includes('Talk') || text.includes('Discuss')) {
      return {
        card: 'bg-purple-50/80 border-purple-200 text-purple-900',
        badge: 'bg-purple-100 text-purple-800 border-purple-200',
        icon: '🗣️',
      };
    }
    return {
      card: 'bg-blue-50/80 border-blue-200 text-blue-900',
      badge: 'bg-blue-100 text-blue-800 border-blue-200',
      icon: '📋',
    };
  };

  return (
    <div className="report-markdown-container text-slate-800 leading-relaxed space-y-4 font-sans">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h2: ({ children }) => {
            const headingText = Array.isArray(children) ? children.join('') : String(children || '');
            const style = getHeadingStyle(headingText);
            const cleanTitle = headingText.replace(/^[📋📊⚠️💡🎾🗣️\s]+/, '').replace(/^##\s*/, '');
            return (
              <div className={`rounded-xl px-4 py-3 mt-6 mb-3 border flex items-center gap-2.5 font-bold text-base shadow-2xs ${style.card}`}>
                <span className="text-xl">{style.icon}</span>
                <span className="flex-1 tracking-tight">{cleanTitle}</span>
              </div>
            );
          },
          h3: ({ children }) => (
            <h3 className="text-sm font-bold text-slate-900 mt-4 mb-2 flex items-center gap-2 border-b border-slate-100 pb-1">
              {children}
            </h3>
          ),
          h4: ({ children }) => (
            <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider mt-3 mb-1">
              {children}
            </h4>
          ),
          p: ({ children }) => (
            <p className="text-sm text-slate-700 leading-relaxed my-2.5">
              {children}
            </p>
          ),
          ul: ({ children }) => (
            <ul className="my-2.5 space-y-2 pl-0 list-none">
              {children}
            </ul>
          ),
          ol: ({ children }) => (
            <ol className="my-2.5 space-y-2.5 pl-0 list-none">
              {children}
            </ol>
          ),
          li: ({ children }) => (
            <li className="bg-slate-50/70 border border-slate-200/80 rounded-xl p-3.5 my-2 text-sm text-slate-700 leading-relaxed shadow-2xs">
              {children}
            </li>
          ),
          blockquote: ({ children }) => (
            <blockquote className="border-l-4 border-amber-400 bg-amber-50/70 rounded-r-xl p-3.5 my-3 text-sm text-amber-950 italic">
              {children}
            </blockquote>
          ),
          strong: ({ children }) => (
            <strong className="font-bold text-slate-950">
              {children}
            </strong>
          ),
          table: ({ children }) => (
            <div className="overflow-x-auto my-4 rounded-xl border border-slate-200 shadow-2xs">
              <table className="w-full text-left text-xs border-collapse bg-white divide-y divide-slate-200">
                {children}
              </table>
            </div>
          ),
          thead: ({ children }) => (
            <thead className="bg-slate-100 text-slate-900 font-bold">
              {children}
            </thead>
          ),
          th: ({ children }) => (
            <th className="p-3 font-bold text-slate-900 border-b border-slate-200 text-xs">
              {children}
            </th>
          ),
          tbody: ({ children }) => (
            <tbody className="divide-y divide-slate-100 bg-white">
              {children}
            </tbody>
          ),
          td: ({ children }) => (
            <td className="p-3 text-slate-700 text-xs leading-relaxed align-top">
              {children}
            </td>
          ),
          tr: ({ children }) => (
            <tr className="hover:bg-slate-50/70 transition-colors">
              {children}
            </tr>
          ),
          code: ({ children, inline }) => {
            if (inline) {
              return <code className="bg-slate-100 text-slate-800 px-1.5 py-0.5 rounded text-xs font-mono">{children}</code>;
            }
            return (
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-3.5 my-2.5 text-sm text-slate-700 leading-relaxed">
                {children}
              </div>
            );
          },
          pre: ({ children }) => (
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-3.5 my-2.5 text-sm text-slate-700 leading-relaxed">
              {children}
            </div>
          )
        }}
      >
        {cleaned}
      </ReactMarkdown>
    </div>
  );
};

const AIAnalysisModal = ({
  isOpen,
  onClose,
  analysisType = 'match', // 'match' | 'player_overall'
  matchId = null,
  playerId = 'me',
  playerName = 'Me',
  subtitle = '',
  statsSnapshot = null,
  onAnalysisSaved = null,
}) => {
  const [loadingSaved, setLoadingSaved] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [analyses, setAnalyses] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [error, setError] = useState(null);

  const pollTimerRef = useRef(null);
  const pollCountRef = useRef(0);

  const stopPolling = () => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    pollCountRef.current = 0;
  };

  const startPolling = useCallback(() => {
    if (pollTimerRef.current) return;
    pollCountRef.current = 0;
    pollTimerRef.current = setInterval(() => {
      pollCountRef.current += 1;
      if (pollCountRef.current > 24) {
        // 24 * 2.5s = 60s
        stopPolling();
        setGenerating(false);
        setError('AI Coach generation is taking longer than expected. Please click "Force Re-Analyze" to start a fresh analysis.');
        return;
      }
      fetchSavedAnalyses(true);
    }, 2500);
  }, []);

  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, []);

  const fetchSavedAnalyses = useCallback(async (isPolling = false) => {
    if (!isOpen) return;
    if (!isPolling) setLoadingSaved(true);
    if (!isPolling) setError(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        handleAuthError();
        return;
      }

      const params = new URLSearchParams({
        analysis_type: analysisType,
      });
      if (matchId && matchId !== 'all') {
        params.append('match_id', matchId);
      } else if (playerId) {
        params.append('player_id', playerId);
      }
      params.append('_t', Date.now().toString());

      const res = await fetch(`${API_BASE_URL}/get_ai_analyses.php?${params.toString()}`, {
        cache: 'no-store',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Pragma': 'no-cache',
          'Cache-Control': 'no-cache',
        }
      });

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.message || 'Failed to retrieve saved analyses.');
      }

      const data = await res.json();
      if (data && data.success) {
        const list = data.analyses || [];
        setAnalyses(list);

        const latest = list[0];
        if (latest && latest.status === 'processing') {
          setGenerating(true);
          startPolling();
        } else {
          setGenerating(false);
          stopPolling();
          if (latest && latest.status === 'failed') {
            setError(latest.error_message || 'AI Coach analysis could not be completed.');
          }
        }
        if (!isPolling) {
          setSelectedIndex(0);
        }
      }
    } catch (err) {
      console.error('Error loading AI analyses:', err);
      if (!isPolling) {
        setError(err.message || 'Could not load existing analyses.');
      }
    } finally {
      if (!isPolling) setLoadingSaved(false);
    }
  }, [isOpen, analysisType, matchId, playerId, startPolling]);

  useEffect(() => {
    if (isOpen) {
      fetchSavedAnalyses();
    } else {
      stopPolling();
    }
  }, [isOpen, fetchSavedAnalyses]);

  const handleGenerate = async (force = false) => {
    setGenerating(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        handleAuthError();
        return;
      }

      const payload = {
        analysis_type: analysisType,
        match_id: matchId && matchId !== 'all' ? matchId : null,
        player_id: playerId || 'me',
        player_name: playerName || 'Me',
        stats_snapshot: statsSnapshot || {},
        force: Boolean(force),
      };

      const res = await fetch(`${API_BASE_URL}/generate_ai_analysis.php`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data?.message || 'Failed to generate AI analysis.');
      }

      if (data.status === 'completed' && data.analysis) {
        setAnalyses(prev => [data.analysis, ...prev]);
        setSelectedIndex(0);
        setGenerating(false);
        stopPolling();
        if (typeof onAnalysisSaved === 'function') {
          onAnalysisSaved(data.analysis);
        }
      } else {
        // Status is 'processing' on server -> begin polling
        startPolling();
      }
    } catch (err) {
      console.error('AI Generation Error:', err);
      setError(err.message || 'Error occurred while communicating with AI service.');
      setGenerating(false);
      stopPolling();
    }
  };

  const handleExportPDF = () => {
    const activeAnalysis = analyses[selectedIndex];
    if (!activeAnalysis) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Please allow popups to export the PDF report.');
      return;
    }

    const titleText = escapeHtml(activeAnalysis.title || `${playerName} - AI Coaching Analysis`);
    const dateText = escapeHtml(new Date(activeAnalysis.created_at || Date.now()).toLocaleDateString());
    const safePlayerName = escapeHtml(playerName);
    const safeReportType = escapeHtml(analysisType === 'match' ? 'Match Breakdown' : 'Player Career Analytics');

    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>${titleText}</title>
          <style>
            @page {
              size: A4;
              margin: 18mm 15mm 18mm 15mm;
            }
            body {
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
              color: #1e293b;
              line-height: 1.5;
              margin: 0;
              padding: 10px;
            }
            .header {
              border-bottom: 2px solid #2563eb;
              padding-bottom: 12px;
              margin-bottom: 20px;
              display: flex;
              justify-content: space-between;
              align-items: center;
            }
            .logo {
              font-size: 20px;
              font-weight: 800;
              color: #2563eb;
              letter-spacing: -0.5px;
            }
            .meta {
              text-align: right;
              font-size: 11px;
              color: #64748b;
            }
            h1 {
              font-size: 20px;
              font-weight: 800;
              margin: 0 0 4px 0;
              color: #0f172a;
            }
            .badge {
              display: inline-block;
              background-color: #eff6ff;
              color: #1d4ed8;
              font-size: 11px;
              font-weight: 700;
              padding: 3px 8px;
              border-radius: 6px;
              margin-top: 4px;
            }
            .card {
              background: #ffffff;
              border: 1px solid #e2e8f0;
              border-radius: 8px;
              padding: 14px 16px;
              margin-bottom: 14px;
              page-break-inside: avoid;
            }
            .card-title {
              font-size: 14px;
              font-weight: 700;
              margin-bottom: 8px;
              padding-bottom: 6px;
              border-bottom: 1px solid rgba(0,0,0,0.06);
            }
            .card-overview { border-left: 4px solid #3b82f6; background-color: #f8fafc; }
            .card-overview .card-title { color: #1d4ed8; }
            .card-issues { border-left: 4px solid #ef4444; background-color: #fff1f2; }
            .card-issues .card-title { color: #be123c; }
            .card-improvements { border-left: 4px solid #10b981; background-color: #f0fdf4; }
            .card-improvements .card-title { color: #047857; }
            .card-drills { border-left: 4px solid #6366f1; background-color: #eef2ff; }
            .card-drills .card-title { color: #4338ca; }
            .card-coach { border-left: 4px solid #a855f7; background-color: #faf5ff; }
            .card-coach .card-title { color: #7e22ce; }
            .report-item-print {
              display: flex;
              align-items: flex-start;
              gap: 10px;
              background: #ffffff;
              border: 1px solid #e2e8f0;
              border-radius: 6px;
              padding: 8px 12px;
              margin-bottom: 6px;
              page-break-inside: avoid;
            }
            .report-badge-print {
              font-size: 10.5px;
              font-weight: 700;
              padding: 2px 6px;
              border-radius: 4px;
              white-space: nowrap;
              margin-top: 1px;
            }
            .badge-overview { background: #dbeafe; color: #1e40af; }
            .badge-issues { background: #ffe4e6; color: #9f1239; }
            .badge-improvements { background: #dcfce7; color: #166534; }
            .badge-drills { background: #e0e7ff; color: #3730a3; }
            .badge-coach { background: #f3e8ff; color: #6b21a8; }
            .pdf-table-container {
              margin: 10px 0 12px 0;
              border: 1px solid #e2e8f0;
              border-radius: 6px;
              overflow: hidden;
              page-break-inside: avoid;
            }
            .pdf-table {
              width: 100%;
              border-collapse: collapse;
              font-size: 11px;
              background: #ffffff;
            }
            .pdf-table th {
              background-color: #f1f5f9;
              color: #0f172a;
              font-weight: 700;
              text-align: left;
              padding: 7px 10px;
              border-bottom: 1px solid #cbd5e1;
            }
            .pdf-table td {
              padding: 7px 10px;
              border-bottom: 1px solid #f1f5f9;
              color: #334155;
              vertical-align: top;
              line-height: 1.45;
            }
            .pdf-table tr:nth-child(even) td {
              background-color: #f8fafc;
            }
            .pdf-table tr:last-child td {
              border-bottom: none;
            }
            p {
              margin: 6px 0;
              font-size: 12.5px;
              line-height: 1.55;
            }
            strong {
              color: #0f172a;
            }
            .footer {
              margin-top: 30px;
              border-top: 1px solid #e2e8f0;
              padding-top: 10px;
              font-size: 10px;
              color: #94a3b8;
              text-align: center;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div>
              <div class="logo">🎾 Best 3 in Tennis</div>
              <h1>${titleText}</h1>
              <span class="badge">Report: AI Coach</span>
            </div>
            <div class="meta">
              <div>Date: ${dateText}</div>
              <div>Report Type: ${safeReportType}</div>
              <div>Target: ${safePlayerName}</div>
            </div>
          </div>

          <div class="content">
            ${formatRawToPrintHtml(activeAnalysis.analysis_content)}
          </div>

          <div class="footer">
            Generated by Best3inTennis AI Coach • Page 1 of 1
          </div>

          <script>
            window.onload = function() {
              window.print();
            };
          </script>
        </body>
      </html>
    `;

    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const formatRawToPrintHtml = (markdown) => {
    if (!markdown) return '';
    const cleaned = cleanAnalysisMarkdown(markdown);
    const lines = cleaned.split('\n');
    let out = '';
    let inCard = false;
    let currentCardType = 'overview';
    let itemIndex = 1;
    let tableBuffer = [];

    const flushTable = () => {
      if (tableBuffer.length === 0) return;
      
      let html = '<div class="pdf-table-container"><table class="pdf-table">';
      let isHeader = true;

      tableBuffer.forEach(rowLine => {
        // Skip separator row |:---|:---|
        if (/^\|[\s\-:|]+\|$/.test(rowLine.trim())) {
          isHeader = false;
          return;
        }

        const cells = rowLine
          .split('|')
          .slice(1, -1)
          .map(c => formatInlineMarkdown(c.trim()));

        if (cells.length === 0) return;

        if (isHeader) {
          html += '<thead><tr>';
          cells.forEach(c => {
            html += `<th>${c}</th>`;
          });
          html += '</tr></thead><tbody>';
          isHeader = false;
        } else {
          html += '<tr>';
          cells.forEach(c => {
            html += `<td>${c}</td>`;
          });
          html += '</tr>';
        }
      });

      html += '</tbody></table></div>';
      out += html;
      tableBuffer = [];
    };

    lines.forEach(l => {
      const line = l.trim();

      if (line.startsWith('|') && line.endsWith('|')) {
        tableBuffer.push(line);
        return;
      }

      if (tableBuffer.length > 0) {
        flushTable();
      }

      if (line.startsWith('## ') || line.startsWith('### ')) {
        if (inCard) { out += '</div>'; }
        const rawTitle = line.replace(/^#+\s*/, '');
        const title = formatInlineMarkdown(rawTitle);
        let cardClass = 'card card-overview';
        currentCardType = 'overview';

        if (rawTitle.includes('Performance Breakdown') || rawTitle.includes('Main Issues') || rawTitle.includes('Issue') || rawTitle.includes('Weakness') || rawTitle.includes('Mistake')) {
          cardClass = 'card card-issues';
          currentCardType = 'issues';
        } else if (rawTitle.includes('Improvement') || rawTitle.includes('Recommendation') || rawTitle.includes('Tactical')) {
          cardClass = 'card card-improvements';
          currentCardType = 'improvements';
        } else if (rawTitle.includes('Drill') || rawTitle.includes('Practice')) {
          cardClass = 'card card-drills';
          currentCardType = 'drills';
        } else if (rawTitle.includes('Coach') || rawTitle.includes('Talk') || rawTitle.includes('Discuss')) {
          cardClass = 'card card-coach';
          currentCardType = 'coach';
        }

        itemIndex = 1;
        out += `<div class="${cardClass}"><div class="card-title">${title}</div>`;
        inCard = true;
      } else if (line.startsWith('- ') || line.startsWith('* ') || line.startsWith('• ') || /^\d+\.\s/.test(line)) {
        const item = formatInlineMarkdown(line.replace(/^[-*•]\s+/, '').replace(/^\d+\.\s+/, ''));
        const badgeLabel = currentCardType === 'issues' ? '📊 ' + itemIndex :
                           currentCardType === 'improvements' ? '💡 ' + itemIndex :
                           currentCardType === 'drills' ? '🎾 ' + itemIndex :
                           currentCardType === 'coach' ? '🗣️ ' + itemIndex :
                           '#' + itemIndex;
        out += `
          <div class="report-item-print">
            <span class="report-badge-print badge-${currentCardType}">${badgeLabel}</span>
            <div style="flex: 1; font-size: 12px; color: #1e293b;">${item}</div>
          </div>
        `;
        itemIndex++;
      } else if (line.length > 0) {
        const formatted = formatInlineMarkdown(line);
        out += `<p>${formatted}</p>`;
      }
    });

    if (tableBuffer.length > 0) {
      flushTable();
    }

    if (inCard) out += '</div>';

    return out;
  };

  if (!isOpen) return null;

  const completedAnalyses = analyses.filter(a => a.status === 'completed' || (!a.status && a.analysis_content));
  const currentAnalysis = completedAnalyses[selectedIndex] || null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col border border-gray-200 overflow-hidden animate-in fade-in zoom-in-95 duration-150">
        {/* Modal Header */}
        <div className="px-6 py-4 bg-slate-900 text-white flex justify-between items-center border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-600/30 border border-blue-400/40 flex items-center justify-center text-xl">
              🤖
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-bold text-white">
                  {analysisType === 'match' ? 'AI Match Performance Analysis' : 'AI Career Coaching Analysis'}
                </h2>
                <span className="bg-blue-500/20 text-blue-300 text-xs font-semibold px-2 py-0.5 rounded-full border border-blue-400/30">
                  AI Coach
                </span>
              </div>
              <p className="text-xs text-slate-400 mt-0.5">
                {subtitle || (analysisType === 'match' ? `Match breakdown for ${playerName}` : `Career dossier for ${playerName}`)}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-white p-2 rounded-lg hover:bg-slate-800 transition text-lg"
            aria-label="Close modal"
          >
            ✕
          </button>
        </div>

        {/* Action / History Bar */}
        <div className="px-6 py-3 bg-slate-50 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            {completedAnalyses.length > 1 && (
              <div className="flex items-center gap-2 text-xs text-gray-600">
                <span className="font-semibold">Version History:</span>
                <select
                  value={selectedIndex}
                  onChange={(e) => setSelectedIndex(Number(e.target.value))}
                  className="bg-white border border-gray-300 rounded-lg px-2.5 py-1 text-xs font-medium text-gray-800 shadow-2xs focus:ring-2 focus:ring-blue-500 focus:outline-none"
                >
                  {completedAnalyses.map((a, idx) => (
                    <option key={a.id || idx} value={idx}>
                      {new Date(a.created_at).toLocaleDateString()} {new Date(a.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} (AI Coach)
                    </option>
                  ))}
                </select>
              </div>
            )}
            {completedAnalyses.length === 1 && (
              <span className="text-xs text-gray-500 font-medium">
                💾 Saved Analysis • {new Date(completedAnalyses[0].created_at).toLocaleDateString()}
              </span>
            )}
            {completedAnalyses.length === 0 && !loadingSaved && !generating && (
              <span className="text-xs text-gray-500 font-medium">
                ✨ Ready for AI Coach
              </span>
            )}
            {generating && (
              <span className="text-xs text-blue-600 font-semibold flex items-center gap-1.5 animate-pulse">
                <span>🎾</span>
                <span>Generating on server...</span>
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            {currentAnalysis && (
              <button
                type="button"
                onClick={handleExportPDF}
                disabled={generating}
                className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold px-3.5 py-1.5 rounded-lg shadow-xs transition flex items-center gap-1.5"
              >
                📄 Export to PDF
              </button>
            )}

            <button
              type="button"
              onClick={handleGenerate}
              disabled={generating}
              className={
                currentAnalysis
                  ? "bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-300 text-xs font-medium px-3 py-1.5 rounded-lg transition flex items-center gap-1.5"
                  : "bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white text-xs font-bold px-4 py-2 rounded-lg shadow-xs transition flex items-center gap-1.5"
              }
            >
              {generating ? (
                <>
                  <span className="animate-spin text-sm">🎾</span>
                  <span>AI Coach is analyzing...</span>
                </>
              ) : currentAnalysis ? (
                <>
                  <span>🔄</span>
                  <span>Re-analyze with AI Coach</span>
                </>
              ) : (
                <>
                  <span>⚡</span>
                  <span>Get Analysis from AI Coach</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div className="p-6 overflow-y-auto flex-1 max-h-[calc(90vh-140px)]">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-start gap-2">
                <span className="text-lg">⚠️</span>
                <p className="font-semibold text-xs sm:text-sm">{error}</p>
              </div>
              <button
                type="button"
                onClick={() => handleGenerate(true)}
                className="self-start sm:self-auto bg-red-600 hover:bg-red-700 text-white text-xs font-bold px-3 py-1.5 rounded-lg transition shadow-2xs whitespace-nowrap cursor-pointer flex items-center gap-1"
              >
                <span>⚡</span>
                <span>Force Re-Analyze</span>
              </button>
            </div>
          )}

          {generating && (
            <div className="py-16 px-4 text-center">
              <div className="inline-block p-4 rounded-full bg-blue-50 border border-blue-200 text-3xl mb-4 animate-bounce">
                🎾
              </div>
              <h3 className="text-base font-bold text-gray-900 mb-1">
                AI Coach is analyzing performance data...
              </h3>
              <p className="text-xs text-gray-500 max-w-md mx-auto mb-3">
                Evaluating winner/mistake ratios, serve hold dynamics, rally breakdown patterns, and generating tactical coaching recommendations.
              </p>
              <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-50/80 border border-blue-200 text-blue-700 text-xs font-medium rounded-full mb-4">
                <span>⚡</span>
                <span>Server-side generation active. You can navigate away safely; your progress will continue.</span>
              </div>
              <div className="flex justify-center gap-3">
                <button
                  type="button"
                  onClick={() => handleGenerate(true)}
                  className="text-xs font-semibold px-4 py-2 rounded-lg bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 transition shadow-2xs flex items-center gap-1.5 cursor-pointer"
                >
                  <span>🔄</span>
                  <span>Taking too long? Force Re-Analyze</span>
                </button>
              </div>
            </div>
          )}

          {!generating && loadingSaved && (
            <div className="py-16 text-center text-gray-500 text-sm">
              <span className="animate-spin inline-block text-xl mb-2">⏳</span>
              <p>Checking database for saved AI analysis...</p>
            </div>
          )}

          {!generating && !loadingSaved && analyses.length === 0 && (
            <div className="py-12 px-4 text-center">
              <div className="w-16 h-16 rounded-2xl bg-blue-50 border border-blue-200 flex items-center justify-center text-2xl mx-auto mb-4">
                🤖
              </div>
              <h3 className="text-base font-bold text-gray-900 mb-1">
                No AI Analysis Generated Yet
              </h3>
              <p className="text-xs text-gray-500 max-w-md mx-auto mb-6">
                Click the button below to get an in-depth tactical analysis from your AI Coach. It will be saved permanently for future review and PDF export.
              </p>
              <button
                type="button"
                onClick={handleGenerate}
                className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-6 py-2.5 rounded-xl shadow-md transition inline-flex items-center gap-2"
              >
                <span>⚡</span>
                <span>Get Analysis from AI Coach</span>
              </button>
            </div>
          )}

          {!generating && !loadingSaved && currentAnalysis && (
            <div>
              {/* Snapshot Banner */}
              {currentAnalysis.stats_snapshot && (
                <div className="bg-slate-900 text-white rounded-xl p-4 mb-6 shadow-2xs flex flex-wrap justify-between items-center gap-3">
                  <div>
                    <h4 className="font-bold text-sm text-white flex items-center gap-2">
                      <span>🎾</span>
                      <span>{currentAnalysis.stats_snapshot.match_name || currentAnalysis.title}</span>
                    </h4>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {currentAnalysis.stats_snapshot.score ? `Score: ${currentAnalysis.stats_snapshot.score} • ` : ''}
                      {currentAnalysis.stats_snapshot.date ? `${new Date(currentAnalysis.stats_snapshot.date).toLocaleDateString()} • ` : ''}
                      Report: AI Coach
                    </p>
                  </div>

                  {currentAnalysis.stats_snapshot.win_rate !== undefined && (
                    <div className="flex items-center gap-4 text-center">
                      <div>
                        <span className="block text-lg font-bold text-sky-400">{currentAnalysis.stats_snapshot.win_rate}%</span>
                        <span className="text-[10px] text-slate-400 uppercase font-semibold">Win Rate</span>
                      </div>
                      <div className="w-px h-8 bg-slate-800" />
                      <div>
                        <span className="block text-lg font-bold text-emerald-400">
                          {currentAnalysis.stats_snapshot.matches_won} - {currentAnalysis.stats_snapshot.matches_lost}
                        </span>
                        <span className="text-[10px] text-slate-400 uppercase font-semibold">W - L</span>
                      </div>
                    </div>
                  )}

                  {currentAnalysis.stats_snapshot.outcome && (
                    <div className="bg-slate-800 px-3 py-1.5 rounded-lg border border-slate-700 text-xs font-bold text-emerald-400">
                      {currentAnalysis.stats_snapshot.outcome}
                    </div>
                  )}
                </div>
              )}

              {/* Formatted Markdown Report Content */}
              <ReportMarkdownRenderer content={currentAnalysis.analysis_content} />
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 flex justify-between items-center text-xs text-gray-500">
          <span>
            {currentAnalysis ? `Analysis saved in database (ID #${currentAnalysis.id})` : 'Best3inTennis AI Performance Engine'}
          </span>
          <button
            onClick={onClose}
            className="px-4 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold rounded-lg transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default AIAnalysisModal;
